import { isNetworkProjectType } from '../../../../app/navigation/courier-project-type';
import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { useQueryGetCarrierHierarchy } from '../../../carriers/carriers.queries';
import { useInfiniteQueryCouriersList } from '../../couriers.queries';
import {
  IncompleteCouriersControlsState,
  initialIncompleteCouriersControlsState,
  makeIncompleteCouriersQueryInput
} from '../logic/incomplete-couriers-props.map';

// Module-level so the reference stays stable across renders.
const INTERNAL_GROUNDFLOOR = ['internal-groundfloor'];

const searchParamOptions: UseSearchParamsOptions<IncompleteCouriersControlsState> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  },
  config: {
    debouncedValues: ['searchTerm']
  }
};

export function useIncompleteCouriers(projectID: string) {
  const project = useQueryGetProject(projectID);
  const [formData, queryData, setFormData] = useSearchParams(
    initialIncompleteCouriersControlsState,
    searchParamOptions
  );

  // Incomplete couriers sit in a network's phone groups, so a project beneath a network sends
  // internal users up to the network's list. Others couldn't read the network, so they stay put.
  const internal = useHasUIFeatures(INTERNAL_GROUNDFLOOR);
  const hierarchy = useQueryGetCarrierHierarchy(projectID, { enabled: internal.hasAllFeatures });
  const anchor = hierarchy.data?.anchorProject;
  const networkAbove =
    anchor && project.data && anchor.id !== project.data.id && isNetworkProjectType(anchor.projectType)
      ? anchor
      : undefined;
  // A 404 from the hierarchy means no carrier or network above, so the project lists its own.
  const resolving = !project.data || internal.isLoading || hierarchy.isLoading;

  const result = useInfiniteQueryCouriersList(
    resolving || networkAbove ? undefined : projectID,
    makeIncompleteCouriersQueryInput(queryData)
  );

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: result.hasNextPage || false,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    onScrollToBottom() {
      result.fetchNextPage();
    }
  };

  return {
    project,
    networkAbove,
    resolving,
    result,
    infiniteScroll,
    formData,
    setFormData
  } as const;
}
